import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";

export const LEAVE_BADGE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700", cancelled: "bg-slate-100 text-slate-600" };
export const LEAVE_LABEL = { pending: "Pending", approved: "Approved", rejected: "Rejected", cancelled: "Cancelled" };
export const downloadLeave = (id, name) => api.get(`/leave/${id}/file`, { responseType: "blob" }).then((r) => {
  const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
}).catch((e) => toast.error(errMsg(e)));
const field = "mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm";

function LeaveForm({ types, onDone }) {
  const [f, setF] = useState({ type: "", start: "", end: "", reason: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const allowFile = types.find((t) => t.key === f.type)?.attachment;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.type || !f.start || !f.end || f.reason.trim().length < 5) return toast.error("Choose a type, both dates and give a reason");
    const fd = new FormData(); Object.entries(f).forEach(([k, v]) => fd.append(k, v)); if (allowFile && file) fd.append("file", file);
    setBusy(true);
    try { await api.post("/leave", fd); toast.success("Leave request sent"); setF({ type: "", start: "", end: "", reason: "" }); setFile(null); onDone(); } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4 rounded-lg border bg-card p-5" data-testid="leave-form">
      <h3 className="font-semibold">Request leave</h3>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="text-sm">Leave type<select value={f.type} onChange={set("type")} className={field} data-testid="leave-type"><option value="">Choose…</option>{types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></label>
        <label className="text-sm">Start date<input type="date" value={f.start} onChange={set("start")} className={field} data-testid="leave-start" /></label>
        <label className="text-sm">End date<input type="date" value={f.end} min={f.start} onChange={set("end")} className={field} data-testid="leave-end" /></label>
      </div>
      <label className="block text-sm">Reason<textarea value={f.reason} onChange={set("reason")} rows={3} className={field} data-testid="leave-reason" /></label>
      {allowFile && <label className="block text-sm">Supporting attachment (optional, PDF/JPG/PNG up to 10 MB)<input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0] || null)} className="mt-1 block text-sm" data-testid="leave-file" /></label>}
      <Button type="submit" disabled={busy} data-testid="leave-submit">{busy ? "Sending…" : "Submit request"}</Button>
    </form>
  );
}

export function MyLeave() {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState(null);
  const load = () => api.get("/leave/me").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { api.get("/leave/types").then((r) => setTypes(r.data)); load(); }, []);
  const cancel = (id) => api.post(`/leave/${id}/cancel`).then(() => { toast.success("Request cancelled"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-6" data-testid="my-leave">
      <LeaveForm types={types} onDone={load} />
      <section>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">My requests</h3>
        {!rows ? <p className="text-sm text-muted-foreground">Loading…</p> : !rows.length ? <p className="text-sm text-muted-foreground" data-testid="leave-empty">No leave requests yet.</p> : (
          <div className="space-y-3">{rows.map((r) => (
            <div key={r.id} className="rounded-lg border bg-card p-4" data-testid={`leave-row-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium">{r.type_label} · {fmtDate(r.start)} – {fmtDate(r.end)} <span className="text-sm text-muted-foreground">{`(${r.days} working days)`}</span></div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LEAVE_BADGE[r.status]}`} data-testid={`leave-status-${r.id}`}>{LEAVE_LABEL[r.status]}</span>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm" translate="no">{r.reason}</p>
              {r.attachment && <button type="button" className="mt-2 text-sm text-sky-700 underline" onClick={() => downloadLeave(r.id, r.attachment.name)} data-testid={`leave-file-${r.id}`} translate="no">{r.attachment.name}</button>}
              {r.decided_by && r.status !== "cancelled" && <p className="mt-2 text-sm text-muted-foreground">Decision by <span translate="no">{r.decided_by}</span>{r.decision_note && <>: <span translate="no">{r.decision_note}</span></>}</p>}
              {r.status === "pending" && <Button size="sm" variant="outline" className="mt-3 block" onClick={() => cancel(r.id)} data-testid={`leave-cancel-${r.id}`}>Cancel request</Button>}
            </div>))}</div>)}
      </section>
    </div>
  );
}
